// The top bar's notifications bell, made real (F-NTF-03, spec/16 §2, and `apps/console/DESIGN.md`).
//
// It reads the signed-in member's own inbox at the current organisation — the durable record the
// pipeline writes (`GET /v1/organizations/{o}/notifications`) — shows the most recent as scannable
// cards, marks a dot on the bell when any are unread, and clears them with one "Mark all read" — or one
// at a time: an unread card is a button, and opening it marks that one read (`markNotificationRead`). In
// HQ, where there is no organisation in scope, it falls back to the honest empty state, exactly as the
// placeholder did before. Server state is TanStack Query over the generated client, like every other
// screen; the endpoint carries no permission, so any signed-in member sees their own rows and no one
// else's.
import type { Notification } from '@clavian/api-client';
import { useProblem } from '../api/use-problem';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Popover } from '@clavian/ui-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { useApi } from '../api/client';

import { BellIcon } from './nav-icons';

/** How many recent notifications the bell shows at once. The full inbox is a screen for later. */
const BELL_PAGE = 12;

const notificationKeys = {
  inbox: (organizationId: string) => ['notifications', organizationId, 'inbox'] as const,
};

/** The caller's most recent notifications at this organisation, newest first. */
function useInbox(organizationId: string | undefined) {
  const api = useApi();
  return useQuery<readonly Notification[]>({
    queryKey: notificationKeys.inbox(organizationId ?? ''),
    enabled: organizationId !== undefined,
    queryFn: async () =>
      (await api.listNotifications({ path: { organizationId: organizationId as string }, query: { limit: BELL_PAGE, status: 'all' } })).data,
  });
}

/** Mark every unread notification read, then re-read so the dot clears. */
function useMarkAllRead(organizationId: string | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.markAllNotificationsRead({ path: { organizationId: organizationId as string } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.inbox(organizationId ?? '') }),
  });
}

/** Mark one notification read, and put the answer in the cached inbox so its dot clears at once. */
function useMarkRead(organizationId: string | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => api.markNotificationRead({ path: { organizationId: organizationId as string, notificationId } }),
    onSuccess: (read) =>
      queryClient.setQueryData<readonly Notification[]>(notificationKeys.inbox(organizationId ?? ''), (rows) =>
        rows?.map((row) => (row.id === read.id ? read : row)),
      ),
  });
}

/** A short "5m" style stamp; falls back to a date for anything older than a day. */
function timeAgo(iso: string, t: (key: string, options?: Record<string, unknown>) => string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return t('shell.agoNow');
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t('shell.agoMinutes', { n: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('shell.agoHours', { n: hours });
  return new Date(iso).toLocaleDateString();
}

export function NotificationsBell({ organizationId }: { readonly organizationId?: string }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const inbox = useInbox(organizationId);
  const markAll = useMarkAllRead(organizationId);
  const markRead = useMarkRead(organizationId);

  const items = inbox.data ?? [];
  const unread = items.some((notification) => notification.readAt === null);

  return (
    <span className="console-bell">
      <Popover
        side="bottom"
        align="end"
        label={t('shell.notifications')}
        trigger={<Button variant="ghost" size="sm" className="console-topbar__icon" label={t('shell.notifications')} icon={<BellIcon />} />}
      >
      <div className="console-notifications">
        {organizationId === undefined || (inbox.data !== undefined && items.length === 0) ? (
          <div className="console-notifications__empty">
            <span className="console-notifications__empty-icon" aria-hidden="true">
              <BellIcon />
            </span>
            <p className="console-notifications__title">{t('shell.notificationsEmptyTitle')}</p>
            <p className="console-notifications__body">{t('shell.notificationsEmptyBody')}</p>
          </div>
        ) : inbox.data === undefined && inbox.isError ? (
          <p className="console-notifications__body">{problem.text(inbox.error, { fallback: t('shell.notificationsError') })}</p>
        ) : inbox.data === undefined ? (
          <p className="console-notifications__body">{t('app.loading')}</p>
        ) : (
          <>
            <div className="console-notifications__head">
              <p className="console-notifications__title">{t('shell.notifications')}</p>
              {unread ? (
                <Button variant="ghost" size="sm" busy={markAll.isPending} onClick={() => markAll.mutate()}>
                  {t('shell.notificationsMarkAllRead')}
                </Button>
              ) : null}
            </div>
            <ul className="console-notifications__list">
              {items.map((notification) => (
                <li
                  key={notification.id}
                  className={notification.readAt === null ? 'console-notifications__item console-notifications__item--unread' : 'console-notifications__item'}
                >
                  {notification.readAt === null ? (
                    <button
                      type="button"
                      className="console-notifications__itemButton"
                      aria-label={t('shell.notificationsMarkRead', { title: notification.title })}
                      onClick={() => markRead.mutate(notification.id)}
                    >
                      <NotificationBody notification={notification} time={timeAgo(notification.createdAt, t)} />
                    </button>
                  ) : (
                    <NotificationBody notification={notification} time={timeAgo(notification.createdAt, t)} />
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      </Popover>
      {unread ? <span className="console-bell__badge" aria-hidden="true" /> : null}
    </span>
  );
}

function NotificationBody({ notification, time }: { readonly notification: Notification; readonly time: string }): ReactNode {
  return (
    <>
      <span className="console-notifications__itemHead">
        <span className="console-notifications__itemTitle">{notification.title}</span>
        <span className="console-notifications__time">{time}</span>
      </span>
      <span className="console-notifications__itemBody">{notification.body}</span>
    </>
  );
}
